import { useMemo, type ChangeEvent } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../Dialog';
import PrimaryButton from '../PrimaryButton';
import { alpha3ToFlagEmoji, listCountryOptions } from '@peekapak/country-origin';
import { clearHomeCountryChoice, persistHomeCountryChoice, type CountryOfOrigin } from '@peekapak/country-origin';
import { dataResidencyManager } from '../../DataResidencyManager';
import type { DataResidency, HomeCountry } from '@peekapak/common/lib/regionAndResidency';
import type { HomeCountrySource } from '@peekapak/common/lib/homeCountryResolution';

// Friendly labels for the derived data residency zone (shown for transparency;
// the user never picks residency directly - D1).
const RESIDENCY_LABELS: Record<DataResidency, string> = {
  USA: 'United States',
  ARE: 'United Arab Emirates',
};

// How the current home country was arrived at (detected vs chosen - D1/D5).
const SOURCE_LABELS: Record<HomeCountrySource, string> = {
  explicit: 'Chosen by you',
  geolocation: 'Detected from your location',
  ip: 'Detected from your network',
  default: 'Not detected yet',
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resolution: CountryOfOrigin | null;
  /** Re-resolve after the explicit choice changes (persist / clear). */
  onResolutionChange: () => void;
}

/**
 * Home-country selection dialog (design D6). Shows the inferred country, its
 * derived data residency, and the source; offers manual selection from the full
 * ISO 3166-1 list; persists the explicit choice (which wins on subsequent
 * visits) and re-derives residency on change. It never prompts for geolocation,
 * so it doubles as the manual path when the browser has hard-blocked location.
 */
export default function HomeCountrySelectionDialog({
  open,
  onOpenChange,
  resolution,
  onResolutionChange,
}: Props): JSX.Element {
  const options = useMemo(() => listCountryOptions(), []);

  const countryOfOrigin = resolution?.countryOfOrigin ?? 'UNK';
  const flag = alpha3ToFlagEmoji(countryOfOrigin);
  const dataResidency = resolution?.dataResidency ?? 'USA';
  const source = resolution?.source ?? 'default';
  const isChosen = source === 'explicit';
  const selectValue = countryOfOrigin === 'UNK' ? '' : countryOfOrigin;

  const handleSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    const alpha3 = event.target.value as HomeCountry;
    if (!alpha3) {
      return;
    }
    persistHomeCountryChoice(alpha3);
    onResolutionChange();
  };

  const handleClearOverride = () => {
    clearHomeCountryChoice();
    // Dropping the choice also drops the residency it persisted. This is the
    // one place with the authority to clear storage: the re-resolution that
    // follows (like any re-resolution) only updates in-memory state.
    dataResidencyManager.clearPersistedDataResidency();
    onResolutionChange();
  };

  // Styled after ForgotPasswordDialog, the app's reference dialog: explicit
  // rem sizes (the app's 62.5% root shrinks Tailwind's stock utilities), GT
  // Walsheim families set explicitly (buttons/selects don't inherit fonts and
  // Tailwind preflight is disabled), and PrimaryButton for the action.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='w-[min(95vw,520px)] rounded-[0.6rem] border-0 bg-white p-0 text-[1.6rem] shadow-[0_14px_34px_rgba(0,0,0,0.2)]'
        overlayClassName='bg-black/50'
      >
        <div className='relative px-[2.6rem] pb-[1.2rem] pt-[2.4rem]'>
          <button
            type='button'
            onClick={() => onOpenChange(false)}
            aria-label='Close'
            className='absolute right-[1rem] top-[0.7rem] cursor-pointer appearance-none border-0 bg-transparent p-0 text-[2.4rem] leading-none text-[#242424]'
          >
            x
          </button>
          <DialogTitle className='m-0 text-[2.4rem] [font-family:GT-Walsheim-Pro-Bold]'>Home country</DialogTitle>
        </div>

        <div className='flex flex-col gap-[1.8rem] px-[3rem] pb-[3rem]'>
          <p className='m-0 text-[1.5rem] text-[#3f3f3f] [font-family:GT-Walsheim-Pro-Light]'>
            Your home country determines where your account data is stored. Pick your country below to correct it.
          </p>

          <div className='flex items-center gap-[1rem] text-[1.8rem] text-[#242424] [font-family:GT-Walsheim-Pro-Bold]'>
            <span aria-hidden='true' className='text-[2rem] leading-none'>
              {flag || '🌐'}
            </span>
            <span>{resolution?.countryName || 'Unknown'}</span>
          </div>

          <dl className='m-0 grid grid-cols-[auto_1fr] gap-x-[1.6rem] gap-y-[0.4rem] text-[1.5rem] [font-family:GT-Walsheim-Pro-Light]'>
            <dt className='text-peeka-warmGrey'>Data residency</dt>
            <dd className='m-0 text-[#3f3f3f]'>{RESIDENCY_LABELS[dataResidency]}</dd>
            <dt className='text-peeka-warmGrey'>Source</dt>
            <dd className='m-0 text-[#3f3f3f]'>{SOURCE_LABELS[source]}</dd>
          </dl>

          <label className='flex flex-col gap-[0.6rem] text-[1.4rem] [font-family:GT-Walsheim-Pro-Light]'>
            <span className='text-peeka-warmGrey'>Change home country</span>
            <select
              value={selectValue}
              onChange={handleSelect}
              className='rounded-[0.6rem] border border-solid border-peeka-grey bg-white px-[1.2rem] py-[0.9rem] text-[1.6rem] text-[#3f3f3f] [font-family:GT-Walsheim-Pro-Light] focus:outline-none focus:ring-2 focus:ring-peeka-turquoise'
            >
              <option value='' disabled>
                Select a country
              </option>
              {options.map((option) => (
                <option key={option.alpha3} value={option.alpha3}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>

          <div className='mt-[1.2rem] flex items-center justify-between gap-[2rem]'>
            {isChosen ? (
              <button
                type='button'
                onClick={handleClearOverride}
                className='cursor-pointer appearance-none border-none bg-transparent p-0 text-[1.4rem] font-bold text-black [font-family:GT-Walsheim-Pro-Bold] hover:underline'
              >
                Use detected location
              </button>
            ) : (
              <span />
            )}
            <PrimaryButton small onClick={() => onOpenChange(false)}>
              Done
            </PrimaryButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
